import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  Button,
  Fade,
  Paper,
  Tooltip,
  Divider,
  Stack,
  InputAdornment
} from '@mui/material';
import {
  Send,
  Mic,
  MicOff,
  Phone,
  SmartToy,
  Person,
  VolumeUp,
  History,
  AutoAwesome,
  MoreVert,
  ArrowForwardIos,
  Psychology
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/SocketContext';
import { useVoice } from '../contexts/VoiceContext';
import { useAuth } from '../contexts/AuthContext';
import { useQueryClient } from 'react-query';

const Chat = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sendMessage, messages } = useSocket();
  const { 
    isListening, 
    startListening, 
    stopListening, 
    interimTranscript, 
    fullTranscript,
    resetTranscript,
    speak,
    language
  } = useVoice();
  
  const [currentMessage, setCurrentMessage] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleVoiceInput = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  useEffect(() => {
    if (isListening && fullTranscript) {
      setCurrentMessage(fullTranscript);
    }
  }, [fullTranscript, isListening]);

  const handleSendMessage = (msgOverride) => {
    const text = msgOverride || currentMessage;
    if (text.trim()) {
      sendMessage(text);
      if (isListening) stopListening();
      resetTranscript();
      
      // AI Logic Simulation
      if (user?.rol === 'ganadero') {
        setTimeout(() => {
          const response = generateAssistantResponse(text);
          speak(response);
        }, 800);
      }
      
      queryClient.invalidateQueries(['dashboard', 'stats']);
      setCurrentMessage('');
    }
  };

  const generateAssistantResponse = (message) => {
    const m = message.toLowerCase();
    if (m.includes('hola') || m.includes('kaixo')) return '¡Kaixo! Soy BASERRI-ADITU. Registro de explotación activo y sincronizado. ¿Desea tramitar alguna guía o revisar el estado sanitario?';
    if (m.includes('guía') || m.includes('document')) return 'He localizado 8 guías en fase de emisión y 156 en histórico. ¿Desea generar una nueva guía de salida para matadero?';
    if (m.includes('censo') || m.includes('animal')) {
      const total = messages.length > 50 ? 495 : 494; // Simplified dynamic check or use a ref/context
      return `Censo verificado: ${total} cabezas oficiales. Todo en orden con REGA.`;
    }
    if (m.includes('alerta') || m.includes('aviso')) return 'Atención: 2 alertas prioritarias detectadas. Una vacunación obligatoria pendiente y una restricción operativa por meteorología.';
    return 'Entendido. Estoy procesando su solicitud técnica. Como su asistente oficial, puedo gestionar trámites, censos y alertas en tiempo real. ¿Alguna otra instrucción?';
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, mt: { xs: 0, md: 8 }, height: { xs: 'calc(100vh - 64px)', md: 'calc(100vh - 120px)' }, display: 'flex', gap: 3, overflow: 'hidden' }}>
      {/* Sidebar de sugerencias / historial */}
      <Box sx={{ width: 320, display: { xs: 'none', lg: 'flex' }, flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>Consultas Frecuentes</Typography>
        {[
          { icon: <History />, text: 'Historial de movimientos' },
          { icon: <AutoAwesome />, text: 'Optimización de censo' },
          { icon: <Psychology />, text: 'Análisis sanitario' },
        ].map((item, i) => (
          <Card key={i} className="premium-card clickable" sx={{ p: 2, cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateX(8px)', borderColor: 'var(--emerald-main)' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'var(--slate-bg)', color: 'var(--navy-main)' }}>{item.icon}</Avatar>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.text}</Typography>
            </Box>
          </Card>
        ))}
        <Box sx={{ mt: 'auto', p: 3, bgcolor: 'var(--navy-main)', borderRadius: '24px', color: 'white' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>ASISTENCIA DIRECTA</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 2 }}>Si requiere asistencia técnica oficial inmediata, puede iniciar una llamada de voz.</Typography>
          <Button fullWidth variant="contained" startIcon={<Phone />} sx={{ bgcolor: 'white', color: 'var(--navy-main)', fontWeight: 800, borderRadius: '12px', '&:hover': { bgcolor: '#f0f0f0' } }}>Llamar al Servicio</Button>
        </Box>
      </Box>

      {/* Main Chat Area */}
      <Card sx={{ flex: 1, borderRadius: '32px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-color)' }}>
        {/* Chat Header */}
        <Box sx={{ p: 2, px: 3, bgcolor: 'white', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'var(--emerald-main)', width: 48, height: 48 }}>
              <SmartToy sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, color: 'var(--navy-main)', lineHeight: 1.2 }}>ADITU / Mayoral Digital</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, bgcolor: 'var(--emerald-main)', borderRadius: '50%' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--emerald-main)' }}>CONECTADO / AI V3.5</Typography>
              </Box>
            </Box>
          </Box>
          <Box>
            <IconButton><MoreVert /></IconButton>
          </Box>
        </Box>

        {/* Chat Body */}
        <Box 
          ref={scrollRef}
          sx={{ 
            flex: 1, 
            p: 4, 
            overflowY: 'auto', 
            bgcolor: '#f8fafc', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 3 
          }}
        >
          {messages.length === 0 && (
            <Fade in>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.6 }}>
                <SmartToy sx={{ fontSize: 60, mb: 2, color: 'var(--slate-light)' }} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>Bienvenido, {user?.name || 'Titular'}</Typography>
                <Typography variant="body2">¿En qué puedo asistirle con su explotación hoy?</Typography>
              </Box>
            </Fade>
          )}

          {messages.map((m, i) => (
            <Fade in key={i}>
              <Box sx={{ display: 'flex', justifyContent: m.emisor === 'admin' ? 'flex-start' : 'flex-end', gap: 2 }}>
                {m.emisor === 'admin' && <Avatar sx={{ bgcolor: 'var(--navy-main)', width: 32, height: 32 }}><SmartToy sx={{ fontSize: 18 }} /></Avatar>}
                <Box sx={{ maxWidth: '75%' }}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      px: 3, 
                      borderRadius: m.emisor === 'admin' ? '2px 24px 24px 24px' : '24px 2px 24px 24px',
                      bgcolor: m.emisor === 'admin' ? 'white' : 'var(--navy-main)',
                      color: m.emisor === 'admin' ? 'var(--navy-main)' : 'white',
                      boxShadow: 'var(--shadow-sm)',
                      border: m.emisor === 'admin' ? '1px solid var(--border-color)' : 'none'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.6 }}>{m.texto}</Typography>
                  </Paper>
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block', px: 1, opacity: 0.5, textAlign: m.emisor === 'admin' ? 'left' : 'right' }}>
                    {new Date(m.fecha || m.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
                {m.type !== 'assistant_response' && <Avatar sx={{ bgcolor: 'var(--emerald-main)', width: 32, height: 32 }}><Person sx={{ fontSize: 18 }} /></Avatar>}
              </Box>
            </Fade>
          ))}
        </Box>

        {/* Chat Input */}
        <Box sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid var(--border-color)' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ position: 'relative' }}>
              <IconButton 
                onClick={handleVoiceInput}
                sx={{ 
                  bgcolor: isListening ? 'var(--alert-crimson)' : 'var(--emerald-main)', 
                  color: 'white',
                  width: 52,
                  height: 52,
                  '&:hover': { bgcolor: isListening ? '#b91c1c' : 'var(--forest-main)' },
                  transition: 'all 0.3s'
                }}
              >
                {isListening ? <MicOff /> : <Mic />}
              </IconButton>
              {isListening && (
                <Box sx={{ position: 'absolute', top: -4, left: -4, right: -4, bottom: -4, borderRadius: '50%', border: '2px solid var(--alert-crimson)', animation: 'pulse 1.5s infinite' }} />
              )}
            </Box>
            
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Describa su trámite o solicite información..."
              value={currentMessage}
              onChange={(e) => {
                setCurrentMessage(e.target.value);
                if (isListening) stopListening(); // Stop listening if user starts typing manually
              }}
              onKeyDown={(e) => { 
                if (e.key === 'Enter' && !e.shiftKey) { 
                  e.preventDefault(); 
                  handleSendMessage(); 
                } 
              }}
              InputProps={{
                sx: { borderRadius: '20px', bgcolor: '#f1f5f9', border: 'none', px: 2 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleSendMessage()} disabled={!currentMessage.trim()}><Send sx={{ color: 'var(--navy-main)' }} /></IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
          {isListening && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center', color: 'var(--alert-crimson)', fontWeight: 800 }}>
              {language === 'es-ES' ? 'ESCUCHANDO (ES)...' : 'ENTZUTEN (EU)...'} {interimTranscript || 'Hable ahora...'}
            </Typography>
          )}
        </Box>
      </Card>
      
      {/* Voice Assistant UI Overlay (Optional/Future integration) */}
    </Box>
  );
};

export default Chat;
